import { useContext, useEffect, useMemo, useState } from 'react'

import { makeVar } from '@apollo/client'
import { Box } from '@mui/material'
import { IconColumns2, IconUser } from '@tabler/icons-react'
import { IconFiles } from '@tabler/icons-react'
import { IconPlayerPlay } from '@tabler/icons-react'
import { IconBuildingSkyscraper } from '@tabler/icons-react'
import type { DayObject, RunEntry } from 'types/graphql'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import DayTable from 'src/components/DataTables/DayTable/DayTable'
import { DayContext } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import { NativeObjectTypes, type NativeObjectType } from 'src/lib/objects'

import { SidebarMode } from '../ObjectSidebar/SidebarContext'
import { GET_PENDING_RUN_ENTRIES } from '../queries'
import { getDayObjectsByType } from '../searchIndex'
import useObjects from '../useObjects'

import { buildColumns, getColumnVisibilityModelInitial } from './columns'
import { buildRows } from './rows'
import { tabSetsByObjectType } from './tabs'

// Create reactive variable outside component to persist across renders
export const pendingRunEntriesVar = makeVar<Map<string, RunEntry>>(new Map())

export const DataNavItems = (routes) =>
  routes
    ? [
        {
          icon: <IconPlayerPlay />,
          label: 'Recordings',
          callback: NativeObjectTypes.MeetingRecording,
        },
        {
          icon: <IconBuildingSkyscraper />,
          label: 'Organizations',
          callback: NativeObjectTypes.Organization,
        },
        {
          icon: <IconUser />,
          label: 'People',
          callback: NativeObjectTypes.Contact,
        },
        {
          icon: <IconColumns2 />,
          label: 'Pipelines',
          callback: NativeObjectTypes.Pipeline,
        },
        {
          icon: <IconFiles />,
          label: 'Pages',
          callback: NativeObjectTypes.Page,
        },
        /*
        {
          icon: <IconBolt />,
          label: 'Actions',
          callback: NativeObjectTypes.Action,
        },
        {
          icon: <IconBubbleText />,
          label: 'Instructions',
          callback: NativeObjectTypes.UserContext,
        },
        */
      ]
    : []

const defaultColumnOverrides = []

const ObjectsTable = ({
  objectType: initialObjectType,
  workspaceId,
  objectsFilter,
  columnOverrides = defaultColumnOverrides,
  viewIdInFocus,
}: {
  objectType: NativeObjectType
  workspaceId: string
  objectsFilter?: {
    propertyDefinitionId: string
    operator: 'equals' | 'in'
    value: string[] | string
  }
  columnOverrides?: {
    propertyDefinitionId: string
    columnParameter: string
    parameterValue: any
  }[]
  viewIdInFocus?: string
}) => {
  const { setSidebarObject } = useContext(DayContext)
  const [objectType, setObjectType] =
    useState<NativeObjectType>(initialObjectType)
  const [dayObjects, setDayObjects] = useState<DayObject[] | null>(null)
  const { propertyDefinitions: propertyDefinitionsAllTypes } = useObjects()
  const propertyDefinitions = useMemo(
    () => propertyDefinitionsAllTypes?.[objectType] || {},
    [objectType, propertyDefinitionsAllTypes]
  )

  const { data: runEntryData } = useQuery(GET_PENDING_RUN_ENTRIES, {
    variables: {
      workspaceId,
      objectType,
    },
    pollInterval: 4000,
    skip: !objectType || !workspaceId,
  })
  useEffect(() => {
    const entriesMap = new Map<string, RunEntry>(
      runEntryData?.pendingRunEntries
        ?.filter(Boolean)
        .map((entry) => [
          `${entry.objectType as string}-${entry.objectId as string}-${entry.propertyDefinitionId as string}` as string,
          entry as RunEntry,
        ])
    )
    pendingRunEntriesVar(entriesMap)
  }, [runEntryData])

  useEffect(() => {
    const fetchDayObjects = async () => {
      // **TODO** => Replace this someday with Zero
      const dayObjectResults = await getDayObjectsByType({
        objectType,
        workspaceId,
      })
      setDayObjects(dayObjectResults.map((result) => result.object))
    }
    if (objectType && workspaceId) {
      fetchDayObjects()
    }
  }, [objectType, workspaceId])

  const columns = useMemo(() => {
    if (!propertyDefinitions) return []

    return buildColumns(
      workspaceId,
      objectType,
      propertyDefinitions,
      columnOverrides
    )
  }, [objectType, workspaceId, propertyDefinitions, columnOverrides])

  const rows = useMemo(() => {
    if (!dayObjects || !propertyDefinitions) return []
    return buildRows(objectType, dayObjects, propertyDefinitions, objectsFilter)
  }, [objectType, dayObjects, propertyDefinitions, objectsFilter])

  const columnVisibilityModel = useMemo(
    () => getColumnVisibilityModelInitial(objectType),
    [objectType]
  )

  const data = useMemo(() => {
    if (!dayObjects) {
      return {
        columns,
        rows: [],
        waitingToQuery: true,
      }
    }
    return {
      columns,
      rows,
    }
  }, [columns, rows, dayObjects])

  const tabSets = useMemo(() => {
    return tabSetsByObjectType[objectType] || {}
  }, [objectType])

  const navItems = useMemo(() => {
    return DataNavItems(routes)
  }, [])

  const containerSx = {
    width: '100%',
    height: '100vh',
    overflowY: 'hidden',
    overflowX: 'auto',
    '& .objects-table-container': {
      height: '100vh',
      overflow: 'hidden',
      overflowX: 'hidden',
    },
  }

  return (
    <Box sx={containerSx}>
      <Box
        className="objects-table-container"
        key={`${objectType}-objects-table`}
      >
        <DayTable
          id={`${objectType}-objects-table`}
          workspaceId={workspaceId}
          objectType={objectType}
          data={data}
          viewIdInFocus={viewIdInFocus}
          initialState={{
            columns: {
              columnVisibilityModel,
            },
            sorting: {
              sortModel: [{ field: 'updatedAt', sort: 'desc' }],
            },
            rowGrouping: { model: [] },
            pinnedColumns: { left: [], right: [] },
            filter: { filterModel: { items: [] } },
            density: 'standard',
            pagination: {
              meta: {},
              paginationModel: {
                page: 0,
                pageSize: 100,
              },
            },
          }}
          tabSets={tabSets}
          onRowClick={(params) => {
            if (objectType === NativeObjectTypes.MeetingRecording) {
              navigate(
                routes.workspaceMeetingRecording({
                  workspaceId,
                  recordingId: params.row.id,
                })
              )
            } else {
              const objectToOpen = {
                objectId: params.row.id,
                objectType,
                workspaceId,
                mode: SidebarMode.THREADS,
              }
              logger.dev('ObjectsTable', {
                objectToOpen,
              })
              setSidebarObject(objectToOpen)
            }
          }}
          navItems={navItems}
          onNavEvent={(objectType) => {
            setObjectType(objectType)
          }}
        />
      </Box>
    </Box>
  )
}

export default ObjectsTable
